import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Shield, ShieldCheck, Lock, FileText, ExternalLink, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const isMyBookieDomain = () => !window.location.hostname.match(/repti-?sitter/i);
const getHomePath = () => (isMyBookieDomain() ? '/' : '/mybookie');

const Pill = ({ color, children }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
    {children}
  </span>
);

const TrustCenter = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Update SEO meta tags directly (Helmet doesn't always overwrite static index.html)
    document.title = 'Trust Center · Security & Compliance at MyBookie';
    const setMeta = (selector, content) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };
    setMeta('meta[name="description"]', "MyBookie's live security posture: current security grade, SOC 2 readiness, GDPR posture, bank-level encryption, and downloadable policies. Updated daily.");
    setMeta('meta[property="og:title"]', 'Trust Center · MyBookie');
    setMeta('meta[property="og:description"]', "Live security grade, SOC 2 readiness, and verifiable compliance posture for MyBookie.");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${window.location.origin}/security`);

    axios.get(`${API}/api/mybookie/public/trust-summary`)
      .then(res => { setSummary(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const grade = summary?.grade || 'A+';
  const gradeColor = ['A+', 'A'].includes(grade)
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : ['B'].includes(grade)
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-red-50 text-red-700 border-red-200';

  const lastDaily = summary?.last_daily_audit ? new Date(summary.last_daily_audit).toLocaleDateString() : '—';
  const lastWeekly = summary?.last_weekly_audit ? new Date(summary.last_weekly_audit).toLocaleDateString() : '—';

  const frameworks = [
    { name: 'SOC 2 Type I / II', status: 'in-progress', icon: ShieldCheck, note: 'Controls implemented; observation window in progress' },
    { name: 'GDPR', status: 'compliant', icon: Lock, note: 'DPA available; EU subprocessors listed' },
    { name: 'NACHA (ACH)', status: 'compliant', icon: CheckCircle2, note: '2026 rules implemented (fraud monitoring, return rate tracking)' },
    { name: 'PCI DSS', status: 'inherited', icon: Lock, note: 'Card data never touches MyBookie servers — Stripe-hosted checkout' },
  ];

  const controls = [
    { label: 'Bank-level encryption', detail: 'TLS 1.3 in transit · AES-256 at rest' },
    { label: 'Authentication', detail: 'Bcrypt password hashing · 2FA · JWT with DB-backed session revocation' },
    { label: 'Brute-force protection', detail: 'IP + email rate limiting · automatic account lockouts · real-IP via X-Forwarded-For' },
    { label: 'Audit logging', detail: 'Every login, password change, and sensitive action logged with IP and timestamp' },
    { label: 'Continuous security audit', detail: 'Daily + weekly automated checks (database health, audit log, access controls, vendor exposure)' },
    { label: 'Real-time alerts', detail: 'Critical/high findings emailed to admin within minutes via Resend' },
    { label: 'Bank data isolation', detail: 'Plaid Production tokens encrypted; no raw bank credentials ever stored' },
    { label: 'Backups & continuity', detail: 'MongoDB Atlas automated backups · documented Business Continuity Plan' },
  ];

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      {/* Header */}
      <nav className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(getHomePath())}>
            <div className="w-10 h-10 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#111614] tracking-tight">MyBookie</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/pricing')} className="text-[#5C6661]">Pricing</Button>
            <Button onClick={() => navigate(`${getHomePath()}?signup=1&from=security`)} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="trust-cta-header">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6 border-b border-[#E6E8E6] bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-3">Trust Center</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-[#111614] tracking-tight max-w-3xl">
            Security you can verify, not just trust.
          </h1>
          <p className="text-lg text-[#5C6661] mt-6 max-w-2xl leading-relaxed">
            Live security posture, automated audit results, and downloadable policies — updated daily.
            We believe security posture should be visible to prospects, not hidden behind a sales call.
          </p>

          {/* Live grade card */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="trust-grade-card">
            <div className={`md:col-span-1 p-6 border rounded-sm ${gradeColor}`}>
              <p className="text-xs uppercase tracking-[0.15em] font-medium opacity-70">Current Security Grade</p>
              <p className="text-6xl font-bold mt-2">{loading ? '…' : grade}</p>
              <p className="text-sm mt-2 opacity-80">{loading ? 'Loading live posture…' : `${summary?.score ?? '—'} / 100 composite score`}</p>
            </div>
            <div className="md:col-span-2 p-6 bg-[#F6F5F2] border border-[#E6E8E6] rounded-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#5C6661] font-medium">Last daily audit</p>
                  <p className="text-lg font-medium text-[#111614] mt-1">{lastDaily}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#5C6661] font-medium">Last weekly audit</p>
                  <p className="text-lg font-medium text-[#111614] mt-1">{lastWeekly}</p>
                </div>
              </div>
              <p className="text-sm text-[#5C6661] mt-4 leading-snug">
                Score is computed in real time from automated audits running against our production stack.
                Findings are weighted by severity (Critical −25, High −15, Medium −6, Low −2) — and a 60/40 split
                blends daily + weekly results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Frameworks */}
      <section className="py-16 px-6 border-b border-[#E6E8E6]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-medium text-[#111614] tracking-tight">Compliance frameworks</h2>
          <p className="text-[#5C6661] mt-3 max-w-2xl">Where we stand across the frameworks that matter for SaaS bookkeeping.</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {frameworks.map(f => {
              const Icon = f.icon;
              const statusBadge = f.status === 'compliant'
                ? <Pill color="bg-emerald-50 text-emerald-700">✓ Compliant</Pill>
                : f.status === 'in-progress'
                  ? <Pill color="bg-amber-50 text-amber-700">In progress</Pill>
                  : <Pill color="bg-sky-50 text-sky-700">Inherited via vendor</Pill>;
              return (
                <div key={f.name} className="p-6 bg-white border border-[#E6E8E6] rounded-sm hover:border-[#B4C9BE] transition" data-testid={`framework-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E2ECE7] flex items-center justify-center rounded-sm">
                        <Icon className="w-5 h-5 text-[#0A2B1D]" />
                      </div>
                      <h3 className="text-lg font-medium text-[#111614]">{f.name}</h3>
                    </div>
                    {statusBadge}
                  </div>
                  <p className="text-sm text-[#5C6661] mt-3 leading-snug">{f.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security Controls */}
      <section className="py-16 px-6 border-b border-[#E6E8E6] bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-medium text-[#111614] tracking-tight">Security controls in place today</h2>
          <p className="text-[#5C6661] mt-3 max-w-2xl">Every control below is wired into production code — not a roadmap promise.</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {controls.map(c => (
              <div key={c.label} className="flex items-start gap-3" data-testid={`control-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-[#111614]">{c.label}</p>
                  <p className="text-sm text-[#5C6661] mt-0.5 leading-snug">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document Links + Subprocessor */}
      <section className="py-16 px-6 border-b border-[#E6E8E6]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-medium text-[#111614] tracking-tight">Policies & documents</h2>
          <p className="text-[#5C6661] mt-3 max-w-2xl">Public documents are below. For SOC 2 control mappings or a signed DPA, email <a href="mailto:security@mybookie.ai" className="text-[#0A2B1D] underline">security@mybookie.ai</a>.</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/privacy" className="p-5 bg-white border border-[#E6E8E6] rounded-sm hover:border-[#0A2B1D] transition group" data-testid="link-privacy">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-[#0A2B1D]" />
                <h3 className="font-medium text-[#111614]">Privacy Policy</h3>
              </div>
              <p className="text-sm text-[#5C6661]">How we collect, use, and protect your data.</p>
              <span className="inline-flex items-center gap-1 text-xs text-[#0A2B1D] mt-3 group-hover:underline">Read <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/terms" className="p-5 bg-white border border-[#E6E8E6] rounded-sm hover:border-[#0A2B1D] transition group" data-testid="link-terms">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-[#0A2B1D]" />
                <h3 className="font-medium text-[#111614]">Terms of Service</h3>
              </div>
              <p className="text-sm text-[#5C6661]">Service agreement and acceptable use.</p>
              <span className="inline-flex items-center gap-1 text-xs text-[#0A2B1D] mt-3 group-hover:underline">Read <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/subprocessors" className="p-5 bg-white border border-[#E6E8E6] rounded-sm hover:border-[#0A2B1D] transition group" data-testid="link-subprocessors">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-[#0A2B1D]" />
                <h3 className="font-medium text-[#111614]">Subprocessors</h3>
              </div>
              <p className="text-sm text-[#5C6661]">Vendors that process your data on our behalf.</p>
              <span className="inline-flex items-center gap-1 text-xs text-[#0A2B1D] mt-3 group-hover:underline">View list <ArrowRight className="w-3 h-3" /></span>
            </Link>
          </div>

          <div className="mt-8 p-6 bg-[#0A2B1D] text-white rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="dpa-cta">
            <div>
              <h3 className="font-medium text-lg">Need a signed DPA, SOC 2 report, or security questionnaire response?</h3>
              <p className="text-sm text-[#B4C9BE] mt-1">We turn these around in 24 hours for paid plans.</p>
            </div>
            <a href="mailto:security@mybookie.ai?subject=Security%20documentation%20request" className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0A2B1D] rounded-sm font-medium text-sm hover:bg-[#E2ECE7] transition">
              Email security team <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Report a security issue */}
      <section className="py-12 px-6 bg-[#F6F5F2]">
        <div className="max-w-6xl mx-auto p-6 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-900">Found a security issue?</h3>
            <p className="text-sm text-amber-800 mt-1 leading-snug">
              We take security reports seriously. Email <a href="mailto:security@mybookie.ai" className="underline font-medium">security@mybookie.ai</a> with details.
              We respond within 24 hours and credit responsible disclosure in our changelog.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 bg-white border-t border-[#E6E8E6] text-sm text-[#5C6661]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>© 2026 MyBookie AI LLC · {window.location.host}</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#111614]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#111614]">Terms</Link>
            <Link to="/subprocessors" className="hover:text-[#111614]">Subprocessors</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrustCenter;
